import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import type { ChartDescriptor } from '../charts'
import type { Workspace } from '../../../context/WorkspaceContext'
import type { AnalysisViewMode } from '../../../types/analysis'
import { ChartPopoverContext } from './ChartPopoverContext'
import { ChartPopoverDialog } from './ChartPopoverDialog'
import { MemberChartPanel } from '../mobile/MemberChartPanel'
import { useIsMemberMobile } from '../../../member/useIsMemberMobile'

export const ChartPopoverProvider = ({
  children,
  viewMode = 'LIVE',
  workspace,
  snapshotId,
  isReducedViewer = false,
}: {
  children: ReactNode
  viewMode?: AnalysisViewMode
  workspace?: Workspace
  snapshotId?: string
  isReducedViewer?: boolean
}) => {
  const isMobile = useIsMemberMobile()
  const [activeDescriptor, setActiveDescriptor] = useState<ChartDescriptor | null>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const moduleRegistryRef = useRef(new Map<string, { descriptor: ChartDescriptor; element: HTMLElement }>())

  const closeChart = useCallback(() => {
    setActiveDescriptor(null)
    window.setTimeout(() => returnFocusRef.current?.focus(), 0)
  }, [])

  const openChart = useCallback((descriptor: ChartDescriptor) => {
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    setActiveDescriptor(descriptor)
  }, [])

  const registerModule = useCallback((moduleId: string, descriptor: ChartDescriptor, element: HTMLElement) => {
    moduleRegistryRef.current.set(moduleId, { descriptor, element })
    return () => {
      if (moduleRegistryRef.current.get(moduleId)?.element === element) moduleRegistryRef.current.delete(moduleId)
    }
  }, [])

  const openModule = useCallback(
    (moduleId: string) => {
      const entry = moduleRegistryRef.current.get(moduleId)
      if (!entry) return false
      entry.element.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
      openChart(entry.descriptor)
      return true
    },
    [openChart],
  )

  const value = useMemo(() => ({ activeDescriptor, openChart, closeChart, registerModule, openModule }), [activeDescriptor, closeChart, openChart, openModule, registerModule])

  return (
    <ChartPopoverContext.Provider value={value}>
      {children}
      {isMobile ? (
        <MemberChartPanel descriptor={activeDescriptor} onClose={closeChart} viewMode={viewMode} workspace={workspace} snapshotId={snapshotId} isReducedViewer={isReducedViewer} />
      ) : (
        <ChartPopoverDialog descriptor={activeDescriptor} onClose={closeChart} viewMode={viewMode} workspace={workspace} snapshotId={snapshotId} isReducedViewer={isReducedViewer} />
      )}
    </ChartPopoverContext.Provider>
  )
}
